import { useState } from 'react';
import { useFragment } from '@apollo/client/react';
import { isEmpty } from 'lodash';

import { createRequiredContext } from 'src/context/createRequiredContext';
import { type IEscrowInvitation } from 'src/graphql/schema';
import { EscrowInvitationFields } from 'src/graphql/actions/escrow/escrowInvitation.fragment';

// A new invitation starts by picking its organization, then fills the form
export type IBeneficiaryInvitationMode = 'pick' | 'form' | 'view';

const [useBeneficiaryInvitationContext, BeneficiaryInvitationContext] = createRequiredContext<{
  escrowId: string;
  escrowInvitation: IEscrowInvitation | null;
  mode: IBeneficiaryInvitationMode;
  setMode: (mode: IBeneficiaryInvitationMode) => void;
}>();

type Props = {
  escrowId: string;
  invitationId: string | null;
};

const BeneficiaryInvitationContextProvider = ({ escrowId, invitationId, children }: React.PropsWithChildren<Props>) => {
  const { data, complete } = useFragment<IEscrowInvitation>({
    fragment: EscrowInvitationFields,
    from: { __typename: 'EscrowInvitation', id: invitationId || '' },
  });

  const escrowInvitation = complete && !isEmpty(data) && invitationId ? (data as IEscrowInvitation) : null;

  const [mode, setMode] = useState<IBeneficiaryInvitationMode>(escrowInvitation ? 'view' : 'pick');

  const value = { escrowId, escrowInvitation, mode, setMode };

  return <BeneficiaryInvitationContext value={value}>{children}</BeneficiaryInvitationContext>;
};

export { useBeneficiaryInvitationContext, BeneficiaryInvitationContextProvider };
